import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Exported from the design (5543:40014/40015/40016) rather than reused from the
// tarot deck. The deck ships flat scans; these are perspective renders with the
// gloss and gilt edge the frame relies on, and each card's angle is part of the
// image. A flat card plus a CSS rotate reads noticeably more 2D.
import chariotCard from '../../../../../resources/images/subscription/paywallCardChariot.png';
import hermitCard from '../../../../../resources/images/subscription/paywallCardHermit.png';
import magicianCard from '../../../../../resources/images/subscription/paywallCardMagician.png';
import paywallPetal from '../../../../../resources/images/subscription/paywallPetal.png';
import paywallStarfield from '../../../../../resources/images/subscription/paywallStarfield.png';
import CrossIcon from '../../../../icons/CrossIcon';
import LogoIcon from '../../../../icons/LogoIcon';
import CheckIcon from '../../../../icons/subscriptionIcons/CheckIcon';
import {
    billingFrequencyKey,
    billingMonths,
    defaultAdvertisedPlan,
    formatMoney,
    formatMonthlyPrice,
    formatPeriodPrice,
    introPrice,
    listPrice,
    planSaving,
    sortPlansForDisplay
} from '../../../CheckoutPage/planPricing';
import styles from './styles.module.scss';

// Static marketing copy, not plan data — see the note on `unlimited` in
// translation.js.
//
// HORO-10554: the four items in Figma `5788:2647`, in its order. HORO-10495
// built this list to `4510:25365`, which lists five and omits `memory` — the
// two frames disagree, and this follows the newer one. Worth reconciling: the
// component now also backs TarotLimitModal, so the choice covers both surfaces.
//
// HORO-10647: `loveTarot` added after `zara` — the Love Tarot pill opens this
// paywall, so it has to say what the pill sells. It's a fifth item.
const BENEFIT_KEYS = ['messaging', 'memory', 'zara', 'loveTarot', 'earlyAccess'];

/**
 * HORO-10504/HORO-10495: the "Ask without limits" screen (Figma 4167-16380 /
 * 4510:25365, "Premium subscription_general") — extracted out of
 * MessageLimitPaywall so it can also back TarotLimitModal's registered-user
 * tarot-limit upsell, rather than that surface maintaining its own separate
 * design. Purely presentational: plan selection lives here (self-contained,
 * same as it always did), but *closing* and *committing to a plan* are left
 * to the caller via `onClose`/`renderCta`, since the two callers need
 * different behavior there — MessageLimitPaywall closes via Redux and goes
 * straight to checkout (authed-only surface); TarotLimitModal closes via a
 * local-state prop and, for anonymous users, has to route through sign-up
 * first (checkout is a protected route) rather than checkout directly.
 *
 * `renderCta(selectedPlan)` — a render-prop, not a fixed button — is what
 * makes both fit here without either caller compromising: MessageLimitPaywall
 * still renders the exact same `PaymentButton` it always has (identical
 * fallback-to-`premium` behavior, zero change), while TarotLimitModal renders
 * a plain button that reports the tap (and the selected plan) back up to its
 * own `onUpsellTap` prop.
 */
const AskWithoutLimitsPaywall = ({ plans, onClose, renderCta }) => {
    const { t } = useTranslation();

    // HORO-10554: shortest billing period first, so monthly sits left of yearly
    // (Figma `5788:2647`). The catalogue returns them the other way round.
    const ordered = useMemo(() => sortPlansForDisplay(plans), [plans]);

    // Default to the plan carrying a saving — that's the offer the design leads
    // with — otherwise the first available.
    const [selectedId, setSelectedId] = useState(null);
    const selected = useMemo(() => ordered.find((plan) => plan.id === selectedId) || defaultAdvertisedPlan(ordered), [ordered, selectedId]);

    return (
        <div className={styles.paywall}>
            <div className={styles.inner}>
                <button type="button" className={styles.close} onClick={onClose} aria-label={t('pages.paymentSuccess.close')}>
                    <CrossIcon width={30} height={30} />
                </button>

                {/* Runs across the top on mobile and becomes the right-hand
                    panel on desktop, carrying the wordmark with it — the frames
                    place it inside the artwork in both. */}
                {/* A layer behind the content, not a block above it: the
                    starfield runs under the copy and the gradient dissolves it
                    into the page, exactly as both frames compose it. Card and
                    petal positions are percentages of the frame coordinates, so
                    the scatter holds at any size. */}
                <div className={styles.artwork} aria-hidden="true">
                    <img src={paywallStarfield} alt="" className={styles.starfield} />
                    {/* Grouped so a short viewport can scale the whole
                        cluster at once rather than each card separately. */}
                    <div className={styles.cards}>
                        <img src={magicianCard} alt="" className={styles.cardA} />
                        <img src={hermitCard} alt="" className={styles.cardB} />
                        <img src={chariotCard} alt="" className={styles.cardC} />
                        <img src={paywallPetal} alt="" className={styles.petalA} />
                        <img src={paywallPetal} alt="" className={styles.petalB} />
                        <img src={paywallPetal} alt="" className={styles.petalC} />
                    </div>
                    <span className={styles.fade} />
                </div>

                {/* Same wordmark as the app header — "Veda." and ".com" at
                    weight 300 against "Astrology" at 500. */}
                <LogoIcon className={styles.wordmark} />

                <div className={styles.content}>
                    <div className={styles.heading}>
                        <h2 className={styles.title}>{t('pages.paywall.title')}</h2>
                        <p className={styles.subtitle}>{t('pages.paywall.subtitle')}</p>
                    </div>

                    <ul className={styles.benefits}>
                        {BENEFIT_KEYS.map((key) => (
                            <li key={key} className={styles.benefit}>
                                <CheckIcon className={styles.check} />
                                <span>{t(`pages.paywall.benefits.${key}`)}</span>
                            </li>
                        ))}
                    </ul>

                    {!!ordered.length && (
                        <div className={styles.plans}>
                            {ordered.map((plan) => {
                                const isSelected = plan.id === selected?.id;
                                const frequency = billingFrequencyKey(plan);
                                const isYearly = frequency === 'yearly';
                                // Cross-plan: the design strikes the monthly
                                // plan's price on the yearly card, which no
                                // `basePrice` ever carried.
                                const saving = planSaving(plan, ordered);
                                // The face of the card is always the settled
                                // price; an introductory offer is a line of copy
                                // below it, not a discounted headline, so the
                                // two plans stay comparable and nothing implies
                                // the intro price recurs.
                                const list = listPrice(plan);
                                const intro = introPrice(plan);

                                return (
                                    <button
                                        type="button"
                                        key={plan.id}
                                        className={isSelected ? `${styles.planCard} ${styles.planCardSelected}` : styles.planCard}
                                        onClick={() => setSelectedId(plan.id)}
                                        aria-pressed={isSelected}
                                    >
                                        {!!saving && (
                                            <span className={styles.badge}>
                                                {t('pages.checkout.percentOff', { percent: saving.percent })}
                                            </span>
                                        )}
                                        <span className={styles.planName}>{t(`pages.paywall.frequency.${frequency}`)}</span>
                                        {/* Inline with the price it is struck
                                            against, not stacked above it — the
                                            frame sets them on one baseline. */}
                                        <span className={styles.planPrice}>
                                            {!!saving && (
                                                <s className={styles.basePrice}>{formatMoney(saving.reference, saving.currency)}</s>
                                            )}
                                            {isYearly ? formatMonthlyPrice(list, plan) : formatPeriodPrice(list)}
                                            {isYearly && <span className={styles.perMonth}>{t('pages.checkout.perMonth')}</span>}
                                        </span>
                                        <span className={styles.billedAt}>
                                            {isYearly
                                                ? t('pages.paywall.billedYearlyAt', { price: formatPeriodPrice(list) })
                                                : t(`pages.paywall.billed.${frequency}`)}
                                        </span>
                                        {!!intro && (
                                            <span className={styles.introPrice}>
                                                {t(`pages.checkout.introPrice.${frequency}`, {
                                                    price: formatPeriodPrice(intro),
                                                    months: billingMonths(plan)
                                                })}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className={styles.footer}>
                        {renderCta(selected)}
                        <p className={styles.footnote}>{t('pages.paywall.footnote')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AskWithoutLimitsPaywall;
